import { datetimeItemFilterKeys } from '@/components/common/filters/types';
import { tempoApi } from '@/lib/api/tempoApi';
import type { DatetimeFilterKeys, ItemFiltersType } from '@/lib/api/types/itemFilters';
import type {
  CreateItemRequest,
  Item,
  ItemAggregationRequest,
  ItemAggregationResponse,
  ItemAggregationsResults,
  ItemAncestor,
  ItemDataSet,
  ItemPatchRequest,
  ItemRequestModelField,
  PaginatedItems,
} from '@/lib/api/types/items';
import {
  and,
  containsIgnoreCase,
  equals,
  greaterThan,
  greaterThanOrEqualTo,
  includes,
  isNotNull,
  isNull,
  lessThan,
  lessThanOrEqualTo,
  type Operator,
  or,
  startsWithIgnoreCase,
} from '@/lib/utils/common/rsqlFilters';

export const getOperationType = (key: string, value: string | string[], operator: Operator) => {
  switch (operator) {
    case 'eq':
      if (Array.isArray(value)) {
        return includes(key, value);
      }
      return equals(key, value);
    case 'containsIgnoreCase':
      if (Array.isArray(value)) {
        return null;
      }
      return containsIgnoreCase(key, value);
    case 'startsWithIgnoreCase':
      if (Array.isArray(value)) {
        return null;
      }
      return startsWithIgnoreCase(key, value);
    case 'in':
      if (!Array.isArray(value)) {
        return equals(key, value);
      }
      if (Array.isArray(value) && value.length === 1) {
        return equals(key, value[0] ?? '');
      }
      return includes(key, Array.isArray(value) ? value : [value]);
    case 'null':
      return isNull(key);
    case 'notnull':
      return isNotNull(key);
    case 'gt':
      if (Array.isArray(value)) {
        return null;
      }
      return greaterThan(key, value);
    case 'ge':
      if (Array.isArray(value)) {
        return null;
      }
      return greaterThanOrEqualTo(key, value);
    case 'lt':
      if (Array.isArray(value)) {
        return null;
      }
      return lessThan(key, value);
    case 'le':
      if (Array.isArray(value)) {
        return null;
      }
      return lessThanOrEqualTo(key, value);
    default:
      if (Array.isArray(value)) {
        return null;
      }
      return equals(key, value);
  }
};

const isMultiDescendantFieldSearch = (filter: ItemFiltersType): boolean => {
  const hasDescendantName = filter['descendant.name']?.value;
  const hasDescendantExternalKey = filter['descendant.externalKey']?.value;

  return !!(
    hasDescendantName &&
    hasDescendantExternalKey &&
    filter['descendant.name']?.value === filter['descendant.externalKey']?.value &&
    filter['descendant.name']?.operator === 'containsIgnoreCase' &&
    filter['descendant.externalKey']?.operator === 'containsIgnoreCase'
  );
};

const handleNullFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (filterValue.operator === 'null') {
    return isNull(filterKey);
  }
  if (filterValue.operator === 'notnull') {
    return isNotNull(filterKey);
  }
  if (filterValue.value === null) {
    return isNull(filterKey);
  }
  return undefined;
};

const handleDatetimeFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (!datetimeItemFilterKeys.includes(filterKey as DatetimeFilterKeys)) {
    return undefined;
  }

  if (
    (filterValue.operator === 'in' || filterValue.operator === 'eq') &&
    Array.isArray(filterValue.value) &&
    filterValue.value.length === 2
  ) {
    return and(
      greaterThanOrEqualTo(filterKey, filterValue.value[0] as string),
      lessThanOrEqualTo(filterKey, filterValue.value[1] as string),
    );
  }

  return undefined;
};

const handleStringFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (typeof filterValue.value !== 'string') {
    return undefined;
  }

  if (filterValue.operator === 'containsIgnoreCase') {
    return containsIgnoreCase(filterKey, filterValue.value);
  }
  if (filterValue.operator === 'startsWithIgnoreCase') {
    return startsWithIgnoreCase(filterKey, filterValue.value);
  }
  return equals(filterKey, filterValue.value);
};

const handleArrayFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (!Array.isArray(filterValue.value)) {
    return undefined;
  }

  if (filterValue.value.length === 0) {
    return undefined;
  }

  if (filterValue.value.length === 1 && filterValue.value[0]) {
    return (
      getOperationType(
        filterKey,
        filterValue.value[0].toString(),
        filterValue.operator as Operator,
      ) ?? undefined
    );
  }

  return (
    getOperationType(filterKey, filterValue.value.map(String), filterValue.operator as Operator) ??
    undefined
  );
};

const handleGenericFilter = (
  filterKey: string,
  filterValue: { operator?: string; value?: unknown },
): string | undefined => {
  if (filterValue.value === undefined) {
    return undefined;
  }

  return (
    getOperationType(filterKey, String(filterValue.value), filterValue.operator as Operator) ??
    undefined
  );
};

const buildSingleFilter = (filterKey: string, filterValueRaw: unknown): string | undefined => {
  if (!filterValueRaw || typeof filterValueRaw !== 'object') {
    return undefined;
  }

  const filterValue = filterValueRaw as { operator?: string; value?: unknown };

  return (
    handleNullFilter(filterKey, filterValue) ||
    handleDatetimeFilter(filterKey, filterValue) ||
    handleStringFilter(filterKey, filterValue) ||
    handleArrayFilter(filterKey, filterValue) ||
    handleGenericFilter(filterKey, filterValue)
  );
};

const buildMultiDescendantFieldSearchFilter = (filter: ItemFiltersType): string | undefined => {
  const searchValue = filter['descendant.name']?.value;
  if (!searchValue) return undefined;

  const searchFilter = or(
    containsIgnoreCase('descendant.name', searchValue),
    containsIgnoreCase('descendant.externalKey', searchValue),
  );

  const otherFilters = Object.entries(filter)
    .filter(
      ([filterKey]) => filterKey !== 'descendant.name' && filterKey !== 'descendant.externalKey',
    )
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  const allFilters = [searchFilter, ...otherFilters];
  return allFilters.length > 0 ? and(...allFilters) : undefined;
};

export const buildFilterString = (filter?: ItemFiltersType): string | undefined => {
  if (!filter) return undefined;

  if (isMultiDescendantFieldSearch(filter)) {
    return buildMultiDescendantFieldSearchFilter(filter);
  }

  const filters = Object.entries(filter)
    .map(([filterKey, filterValue]) => buildSingleFilter(filterKey, filterValue))
    .filter((filterResult): filterResult is string => !!filterResult);

  if (filters === undefined || filters.length === 0) return undefined;

  return and(...filters);
};

const buildItemSearchQueryPrams = ({
  cursor,
  filter,
  limit,
  order,
  hierarchical,
}: {
  cursor?: string;
  filter?: ItemFiltersType;
  limit?: number;
  order?: string;
  hierarchical?: boolean;
}) => {
  const params = new URLSearchParams();

  if (cursor) {
    params.set('cursor', cursor);
  }

  if (order) {
    params.set('order', order);
  }

  if (filter) {
    const filterString = buildFilterString(filter);
    if (filterString) {
      params.set('filter', filterString);
    }
  }

  if (limit) {
    params.set('limit', String(limit));
  }

  if (hierarchical !== undefined) {
    params.set('hierarchical', String(hierarchical));
  }

  return params.toString() ? `?${params.toString()}` : '';
};

const getItems = async ({
  cursor = '',
  filter,
  limit,
  order = 'priority',
  hierarchical,
}: {
  cursor?: string;
  filter?: ItemFiltersType;
  limit?: number;
  order?: string;
  hierarchical?: boolean;
}): Promise<PaginatedItems> => {
  const url = `api/v1/item-store/items${buildItemSearchQueryPrams({ cursor, filter, limit, order, hierarchical })}`;
  return await tempoApi().get(url).json();
};

const getItemsDataSet = async ({
  order,
  filter,
}: {
  filter?: ItemFiltersType;
  order?: string;
}): Promise<ItemDataSet> => {
  const url = `api/v1/item-store/items/data-set${buildItemSearchQueryPrams({ filter, order })}`;
  return await tempoApi().get(url).json();
};

const getInsightsItemsDataSet = async ({
  order,
  filterString = '',
}: {
  filterString?: string;
  order?: string;
}): Promise<ItemDataSet> => {
  const url =
    'api/v1/item-store/items/data-set' +
    (order ? `?order=${order}` : '') +
    (filterString ? `?filter=${encodeURIComponent(filterString)}` : '');

  return await tempoApi().get(url).json();
};

const getItemsBulkFetch = async ({
  ids,
  fields,
  filter,
}: {
  ids: number[];
  fields?: ItemRequestModelField[];
  filter?: ItemFiltersType;
}) => {
  const searchParams = new URLSearchParams();
  if (fields && fields.length > 0) {
    for (const field of fields) {
      searchParams.append('fields', field); // multiple values of same key allowed
    }
  }

  const filterString = buildFilterString(filter);
  if (filterString) {
    searchParams.set('filter', filterString);
  }

  return await tempoApi()
    .post('api/v1/item-store/items/bulk-fetch', { json: ids, searchParams })
    .json<Item[]>();
};

const getItemById = async ({ itemId }: { itemId: number }) =>
  await tempoApi().get(`api/v1/item-store/items/${itemId}`).json<Item>();

const getItemAncestors = async ({ itemId }: { itemId: number }) =>
  await tempoApi().get(`api/v1/item-store/items/${itemId}/ancestors`).json<ItemAncestor[]>();

const updateItem = async ({ itemId, data }: { itemId: number; data: ItemPatchRequest }) =>
  await tempoApi().patch(`api/v1/item-store/items/${itemId}`, { json: data }).json<Item>();

const createItem = async (data: CreateItemRequest) =>
  await tempoApi().post('api/v1/item-store/items', { json: data }).json<Item>();

const getItemAggregations = async (
  request: ItemAggregationRequest,
): Promise<ItemAggregationsResults[]> => {
  const response = await tempoApi()
    .post('api/v1/item-store/items/aggregations', { json: request })
    .json<ItemAggregationResponse>();
  return response.data;
};

export const ItemsApi = {
  getItems,
  getItemsDataSet,
  getInsightsItemsDataSet,
  getItemsBulkFetch,
  getItemById,
  getItemAncestors,
  updateItem,
  createItem,
  getItemAggregations,
};
